'use client';

import { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from '@/hooks/useTranslation';
import { responsiveImage, LOGO_WIDTHS, LOGO_SIZES } from '@/lib/responsiveImage';
import s from './LogoMarquee.module.css';
const LOGO_VER = '5';

function LogoImg({ logo, t }) {
  const src = `${logo.file}?v=${LOGO_VER}`;
  // SVG/AVIF have no -160w/-240w/-320w files — never emit srcSet for them
  // (avoids 404s like razorpay-240w.svg, poonawalla-*-240w.avif).
  const img = responsiveImage(src, { widths: LOGO_WIDTHS, sizes: LOGO_SIZES });
  return (
    <img
      src={img.src}
      {...(img.srcSet ? { srcSet: img.srcSet, sizes: img.sizes } : {})}
      alt={t(logo.name)}
      className={s.logo}
      style={logo.h ? { height: logo.h } : undefined}
      loading="lazy"
      decoding="async"
    />
  );
}

const LOGOS = [{
  name: 'AU Small Finance Bank',
  file: '/clients/au-small-finance-bank.webp',
  h: 52
}, {
  name: 'Bandhan Bank',
  file: '/clients/bandhan-bank.webp'
}, {
  name: 'Tata Capital',
  file: '/clients/tata-capital.webp'
}, {
  name: 'Poonawalla Fincorp',
  file: '/clients/poonawalla-fincorp.avif'
}, {
  name: 'LenDenClub',
  file: '/clients/lendenclub.webp'
}, {
  name: 'InstaMoney',
  file: '/clients/instamoney.webp'
}, {
  name: 'Razorpay',
  file: '/clients/razorpay.svg'
}, {
  name: 'Protean',
  file: '/clients/protean.webp'
}, {
  name: 'MediBuddy',
  file: '/clients/medibuddy.webp'
}, {
  name: 'Gleneagles Hospitals',
  file: '/clients/gleneagles-hospitals.webp'
}, {
  name: 'Licious',
  file: '/clients/licious.webp'
}, {
  name: 'Urban Company',
  file: '/clients/urban-company.webp'
}, {
  name: 'Hathway',
  file: '/clients/hathway.webp'
}, {
  name: 'realme',
  file: '/clients/realme.webp'
}, {
  name: 'Tecno',
  file: '/clients/tecno.webp'
}, {
  name: 'Crompton',
  file: '/clients/crompton.webp'
}, {
  name: 'Häfele',
  file: '/clients/hafele.webp'
}, {
  name: 'Senco Gold & Diamonds',
  file: '/clients/senco.webp'
}, {
  name: 'Blackberrys',
  file: '/clients/blackberrys.avif'
}, {
  name: 'Matrimony',
  file: '/clients/matrimony.webp'
}];

// Row 1 gets the first 10 logos, row 2 gets the remaining 10 — no overlap.
// Each row is triplicated so the marquee can loop seamlessly.
const ROW_1 = LOGOS.slice(0, 10);
const ROW_2 = LOGOS.slice(10);
const TRACK_A = [...ROW_1, ...ROW_1, ...ROW_1];
const TRACK_B = [...ROW_2, ...ROW_2, ...ROW_2];
export function LogoMarquee() {
  const [paused, setPaused] = useState(false);
  const { locale, t } = useTranslation();

  const HEADING = {
    en: <>{t("logomarquee.powering")} <span className="gradient-text">{t("logomarquee.enterprise_and_publicsector_intelli")}</span> {t("logomarquee.across_southeast_asia")}</>,
    es: <>{t("logomarquee.desde_las_operaciones_del_centro_de")} <span className="gradient-text">{t("logomarquee.ms_de_350_organizaciones_empresaria")}</span> {t("logomarquee.confan_en_nuestra_plataforma_de_esc")}</>,
    ar: <>{t("logomarquee.xc6a4772")} <span className="gradient-text">{t("logomarquee.350")}</span> {t("logomarquee.8")}</>,
    ja: <>{t("logomarquee.x809b183")} <span className="gradient-text">{t("logomarquee.350_2")}</span>{t("logomarquee.8_2")}</>
  };
  const lang = locale in HEADING ? locale : 'en';
  const isRtl = lang === 'ar';
  return <section className="pt-16 pb-12" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* "Powering enterprise and public-sector intelligence…" heading
          removed per design — the marquee now stands on its own visual. */}
      <div className={s.strip} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <div className={clsx(s.fadeL, 'bg-gradient-to-r from-page to-transparent')} />
        <div className={clsx(s.fadeR, 'bg-gradient-to-l from-page to-transparent')} />
        <div className={s.rows}>
          {/* Row 1 — content scrolls right-to-left (track moves left) */}
          <div className={clsx(s.track, s.trackLtr, paused && s.trackPaused)}>
            {TRACK_A.map((logo, i) => <div key={`a-${i}`} className={s.card}>
                <LogoImg logo={logo} t={t} />
              </div>)}
          </div>
          {/* Row 2 — content scrolls left-to-right (track moves right) */}
          <div className={clsx(s.track, s.trackRtl, paused && s.trackPaused)}>
            {TRACK_B.map((logo, i) => <div key={`b-${i}`} className={s.card}>
                <LogoImg logo={logo} t={t} />
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}
export default LogoMarquee;